<template>
    <Transition name="full-modal">
        <div v-if="isOpen" class="modal-shell backdrop" :style="modalStyle" @click.self="close()">
            <div class="modal-container">
                <div class="modal-header tw-select-none">
                    <h4 class="tw-ml-3">{{ title }}</h4>
                    <button
                        class="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded hover:tw-bg-slate-200"
                        @click="close()"
                    >
                        <i class="mdi mdi-window-close tw-block tw-scale-[1.33]"></i>
                    </button>
                </div>
                <div class="modal-content tw-relative">
                    <slot></slot>
                    <Loader v-if="loading" fill />
                </div>
                <div v-if="$slots.footer" class="modal-footer">
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </Transition>
</template>
<script lang="ts" setup>
import { computed, watch, ref } from 'vue';
import Loader from '@/js/components/controls/Loader.vue';
import { useOverlayZIndex } from '@/js/composables/useOverlayZIndex.ts';

defineProps({
    title: {
        type: String,
        default: 'Modal Title',
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const isOpen = defineModel({
    type: Boolean,
    default: false,
});

const activeZIndex = ref<number>(0);
const modalStyle = computed(() => (activeZIndex.value ? { zIndex: activeZIndex.value } : null));

const open = () => {
    isOpen.value = true;
};

const close = () => {
    isOpen.value = false;
};

watch(
    isOpen,
    (newValue) => {
        if (newValue) {
            allocateZIndex();
        }
    },
    { immediate: true }
);

const allocateZIndex = () => {
    const { activeZIndex: allocatedZIndex, allocateZIndex: allocateOverlayZIndex } = useOverlayZIndex();
    allocateOverlayZIndex();
    activeZIndex.value = allocatedZIndex.value ?? 0;
};

defineExpose({
    open,
    close,
});
</script>
<style lang="scss" scoped>
.backdrop {
    background-color: rgba(0, 0, 0, 0.33);
}

.modal-shell {
    @apply tw-flex tw-items-center tw-justify-center;
    position: fixed;
    inset: 0;

    transition:
        opacity 170ms ease,
        background-color 160ms ease;
}

.modal-container {
    @apply tw-rounded-lg tw-bg-white tw-shadow-lg tw-w-full tw-max-w-5xl tw-mx-auto tw-flex tw-flex-col;
    min-width: min(calc(95vw - 100px), 1400px);
    min-height: min(calc(95vh - 100px), 800px);
    transition: transform 210ms cubic-bezier(0.22, 1, 0.36, 1);

    & > .modal-header {
        @apply tw-rounded-t-lg tw-px-1.5 tw-flex tw-items-center tw-justify-between;

        & > h4 {
            @apply tw-p-0 tw-mt-3 tw-mb-2 tw-text-base;
        }
    }

    & > .modal-content {
        max-height: min(calc(95vh - 100px), 800px);
        @apply tw-overflow-auto;
    }

    & > .modal-footer {
        @apply tw-border-t tw-bg-white tw-rounded-b-lg tw-px-5 tw-py-3;
        margin-top: auto;
    }
}

.full-modal-enter-from,
.full-modal-leave-to {
    opacity: 0;
    background-color: transparent;
}

.full-modal-enter-from > .modal-container,
.full-modal-leave-to > .modal-container {
    transform: translateY(14px) scale(0.98);
}

.full-modal-enter-to > .modal-container,
.full-modal-leave-from > .modal-container {
    transform: translateY(0) scale(1);
}

.full-modal-leave-active {
    transition-duration: 150ms;
    transition-timing-function: ease-in;
}
</style>
